import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import {
  useEmailMsrPurchaseOrder,
  type MsrPurchaseOrderEmail,
} from '~/api/order';
import { useSellerContacts } from '~/api/sellerContacts';
import { AddSellerContactPanel } from '~/components/common/AddSellerContactPanel';
import { ContactCheckboxList } from '~/components/common/ContactCheckboxList';
import { Modal } from '~/components/overlays/Modal';
import { ModalHeader } from '~/components/overlays/ModalHeader';
import { Button, ClipSpinner } from '~/components/ui';
import { SectionPanel } from '~/components/ui/SectionPanel';
import { OutboundEmailStatus } from '~/graphql/graphql';
import { useContactSelection } from '~/hooks/useContactSelection';
import { countsAsEmailed } from '~/pages/order-view/sections/purchaseOrderEmails';
import { formatDate, formatDateTime, toastGraphQLError } from '~/utils';
import { contactLabel, type ContactDetails } from '~/utils/contact';
import type { MsrPurchaseOrderPrintOptions } from '~/utils/msrPurchaseOrderUrl';

const STATE_STYLES: Record<OutboundEmailStatus, string> = {
  [OutboundEmailStatus.Pending]: 'text-warning-700',
  [OutboundEmailStatus.Sent]: 'text-success-700',
  [OutboundEmailStatus.Failed]: 'text-error-600',
};

const STATE_KEYS: Record<OutboundEmailStatus, string> = {
  [OutboundEmailStatus.Pending]:
    'order.email_purchase_order_modal.state_pending',
  [OutboundEmailStatus.Sent]: 'order.email_purchase_order_modal.state_sent',
  [OutboundEmailStatus.Failed]: 'order.email_purchase_order_modal.state_failed',
};

type EmailMsrPurchaseOrderModalProps = Readonly<{
  onClose: () => void;
  orderId: string;
  printOptions: MsrPurchaseOrderPrintOptions;
  purchaseOrderNumber: string;
  sellingOrganizationId: string;
  initialContactIds: string[];
  purchaseOrderEmails: MsrPurchaseOrderEmail[];
}>;

export function EmailMsrPurchaseOrderModal({
  onClose,
  orderId,
  printOptions,
  purchaseOrderNumber,
  sellingOrganizationId,
  initialContactIds,
  purchaseOrderEmails,
}: EmailMsrPurchaseOrderModalProps) {
  const { t } = useTranslation('order');
  const { contacts, status: contactsStatus } = useSellerContacts(
    sellingOrganizationId
  );
  const { mutate: emailPurchaseOrder, isPending: isSending } =
    useEmailMsrPurchaseOrder();

  const handleClose = () => {
    if (isSending) {
      return;
    }
    onClose();
  };

  const send = (contactIds: string[]) => {
    emailPurchaseOrder(
      { orderId, contactIds, printOptions },
      {
        onSuccess: () => {
          toast.success(
            t('order.email_purchase_order_modal.sent', {
              count: contactIds.length,
            })
          );
          onClose();
        },
        onError: toastGraphQLError,
      }
    );
  };

  const renderBody = () => {
    if (contactsStatus === 'loading') {
      return (
        <div className="px-6 py-4">
          <ClipSpinner />
        </div>
      );
    }
    if (contactsStatus === 'failed') {
      return (
        <p className="px-6 py-4 text-sm text-error-600">
          {t('order.email_purchase_order_modal.load_failed')}
        </p>
      );
    }
    return (
      <ContactSelection
        contacts={contacts}
        initialContactIds={initialContactIds}
        purchaseOrderEmails={purchaseOrderEmails}
        sellingOrganizationId={sellingOrganizationId}
        isSending={isSending}
        onCancel={handleClose}
        onSend={send}
      />
    );
  };

  return (
    <Modal isOpen onClose={handleClose}>
      <div
        className="bg-neut-light rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] flex flex-col"
        data-testid="email-purchase-order-modal"
      >
        <ModalHeader
          title={t('order.email_purchase_order_modal.title', {
            purchaseOrderNumber,
          })}
          description={t('order.email_purchase_order_modal.subtitle')}
          onClose={handleClose}
          closeDisabled={isSending}
        />
        {renderBody()}
      </div>
    </Modal>
  );
}

function EmailHistory({
  emails,
}: Readonly<{ emails: MsrPurchaseOrderEmail[] }>) {
  const { t } = useTranslation('order');
  return (
    <section className="mb-4" data-testid="purchase-order-email-history">
      <SectionPanel
        title={t('order.email_purchase_order_modal.history_title')}
        bodyClassName="p-0"
      >
        <ul className="divide-y divide-neut-border">
          {emails.map((email) => (
            <li
              key={email.id}
              className="px-3 py-2 flex flex-wrap items-baseline gap-x-2 text-sm"
            >
              <span className="min-w-0 break-all">{email.recipientTo}</span>
              <span
                className={`text-xs font-medium ${STATE_STYLES[email.status]}`}
              >
                {t(STATE_KEYS[email.status])}
              </span>
              <span className="basis-full text-xs text-neut-sec">
                {email.requestedBy
                  ? t('order.email_purchase_order_modal.sent_by', {
                      date: formatDateTime(email.createdAt),
                      name: contactLabel(email.requestedBy),
                    })
                  : formatDateTime(email.createdAt)}
              </span>
            </li>
          ))}
        </ul>
      </SectionPanel>
    </section>
  );
}

function lastEmailedAtByRecipient(emails: MsrPurchaseOrderEmail[]) {
  const lastEmailedAt = new Map<string, string>();
  for (const email of emails.filter(countsAsEmailed)) {
    const recipient = email.recipientTo.toLowerCase();
    if (!lastEmailedAt.has(recipient)) {
      lastEmailedAt.set(recipient, email.createdAt);
    }
  }
  return lastEmailedAt;
}

function ContactSelection({
  contacts,
  initialContactIds,
  purchaseOrderEmails,
  sellingOrganizationId,
  isSending,
  onCancel,
  onSend,
}: Readonly<{
  contacts: ContactDetails[];
  initialContactIds: string[];
  purchaseOrderEmails: MsrPurchaseOrderEmail[];
  sellingOrganizationId: string;
  isSending: boolean;
  onCancel: () => void;
  onSend: (contactIds: string[]) => void;
}>) {
  const { t } = useTranslation('order');
  const { selectedContactIds, toggleContact, allSelected, toggleSelectAll } =
    useContactSelection(contacts, initialContactIds);
  const lastEmailedAt = lastEmailedAtByRecipient(purchaseOrderEmails);

  const emailedLabel = (contact: ContactDetails) => {
    const emailedAt =
      contact.externalId && lastEmailedAt.get(contact.externalId.toLowerCase());
    if (!emailedAt) {
      return null;
    }
    return (
      <span
        className="shrink-0 text-xs text-neut-sec"
        data-testid={`email-purchase-order-contact-emailed-${contact.id}`}
      >
        {t('order.email_purchase_order_modal.emailed_on', {
          date: formatDate(emailedAt),
        })}
      </span>
    );
  };

  const contactList =
    contacts.length === 0 ? (
      <p className="text-sm text-neut-sec">
        {t('order.email_purchase_order_modal.no_contacts')}
      </p>
    ) : (
      <ul className="space-y-1">
        <li className="px-1 py-2 flex items-center gap-2 border-b border-neut-border">
          <input
            id="email-purchase-order-contacts-all"
            type="checkbox"
            className="h-4 w-4 cursor-pointer"
            checked={allSelected}
            onChange={() => toggleSelectAll(allSelected)}
          />
          <label
            htmlFor="email-purchase-order-contacts-all"
            className="text-sm font-medium w-full cursor-pointer"
          >
            {t('order.email_purchase_order_modal.select_all')}
          </label>
        </li>
        <ContactCheckboxList
          contacts={contacts}
          selectedContactIds={selectedContactIds}
          onToggle={(contact) => toggleContact(contact.id)}
          checkboxIdPrefix="email-purchase-order-contact"
          trailing={emailedLabel}
        />
      </ul>
    );

  return (
    <AddSellerContactPanel
      organizationId={sellingOrganizationId}
      testId="add-contact-button"
      disabled={isSending}
      onAdded={(contact) => toggleContact(contact.id)}
    >
      {({ trigger, form }) => (
        <>
          <div className="flex-1 overflow-y-auto px-6 py-4">
            {form ?? (
              <>
                {purchaseOrderEmails.length > 0 && (
                  <EmailHistory emails={purchaseOrderEmails} />
                )}
                {contactList}
              </>
            )}
          </div>
          {!form && (
            <div className="px-6 py-4 border-t border-neut-border flex flex-wrap items-center gap-3 shrink-0">
              {trigger}
              <Button
                variant="secondary"
                onClick={onCancel}
                disabled={isSending}
              >
                {t('order.email_purchase_order_modal.cancel')}
              </Button>
              <Button
                variant="primary"
                onClick={() => onSend(Array.from(selectedContactIds))}
                loading={isSending}
                disabled={isSending || selectedContactIds.size === 0}
                data-testid="send-purchase-order-email-button"
              >
                {t('order.email_purchase_order_modal.send')}
              </Button>
            </div>
          )}
        </>
      )}
    </AddSellerContactPanel>
  );
}
